<template>
  <div class="ta-view">
    <div class="ta-column">
      <section class="ta-card">
        <h3 class="ta-label">Mis datos</h3>
        <div class="ta-row"><span class="ta-key">Empresa</span><span class="ta-val">{{ account?.tenantName || user?.tenantName }}</span></div>
        <div class="ta-row"><span class="ta-key">Correo</span><span class="ta-val">{{ account?.email || user?.email }}</span></div>
        <div class="ta-field">
          <label class="ta-key" for="ta-name">Nombre</label>
          <input id="ta-name" v-model="profile.name" class="ta-input" maxlength="120" />
        </div>
        <div class="ta-field">
          <label class="ta-key" for="ta-phone">Teléfono (10 dígitos)</label>
          <input id="ta-phone" v-model="profile.phone" class="ta-input" inputmode="tel" maxlength="20" />
        </div>
        <p v-if="profileMsg" class="ta-msg" :class="{ 'ta-msg--error': profileErr }">{{ profileMsg }}</p>
        <button class="ta-btn ta-btn--primary" :disabled="savingProfile" @click="saveProfile">Guardar cambios</button>
      </section>

      <section class="ta-card">
        <h3 class="ta-label">Cambiar contraseña</h3>
        <div class="ta-field">
          <label class="ta-key" for="ta-cur">Contraseña actual</label>
          <input id="ta-cur" v-model="pw.current" type="password" class="ta-input" autocomplete="current-password" />
        </div>
        <div class="ta-field">
          <label class="ta-key" for="ta-new">Nueva contraseña</label>
          <input id="ta-new" v-model="pw.next" type="password" class="ta-input" autocomplete="new-password" />
          <span class="ta-hint">Mínimo 8 caracteres, una mayúscula y un número.</span>
        </div>
        <div class="ta-field">
          <label class="ta-key" for="ta-rep">Repite la nueva contraseña</label>
          <input id="ta-rep" v-model="pw.repeat" type="password" class="ta-input" autocomplete="new-password" />
        </div>
        <p v-if="pwMsg" class="ta-msg" :class="{ 'ta-msg--error': pwErr }">{{ pwMsg }}</p>
        <button class="ta-btn ta-btn--primary" :disabled="savingPw" @click="savePassword">Cambiar contraseña</button>
      </section>

      <section class="ta-card ta-card--row">
        <button class="ta-btn" @click="onLogout"><span class="mdi mdi-logout"></span> Cerrar sesión</button>
        <button class="ta-btn" @click="onLogoutAll">Cerrar sesión en todos los dispositivos</button>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/services/authStore'
import { useTenantPortalStore } from '@/services/tenantPortalStore'
import { getPortalAccount, updatePortalAccount, changePortalPassword, logoutAllPortalSessions } from '@/services/tenantPortal'

const router = useRouter()
const { state, logout, refreshUser } = useAuthStore()
const { resetTenantPortal } = useTenantPortalStore()
const user = computed(() => state.user)

const account = ref(null)
const profile = reactive({ name: '', phone: '' })
const pw = reactive({ current: '', next: '', repeat: '' })
const savingProfile = ref(false)
const savingPw = ref(false)
const profileMsg = ref('')
const profileErr = ref(false)
const pwMsg = ref('')
const pwErr = ref(false)

onMounted(async () => {
  try {
    account.value = await getPortalAccount()
    profile.name = account.value.name || ''
    profile.phone = account.value.phone || ''
  } catch {
    profileMsg.value = 'No se pudieron cargar tus datos.'
    profileErr.value = true
  }
})

async function saveProfile() {
  profileMsg.value = ''
  profileErr.value = false
  savingProfile.value = true
  try {
    await updatePortalAccount({ name: profile.name, phone: profile.phone })
    await refreshUser()
    profileMsg.value = 'Datos guardados.'
  } catch (err) {
    profileErr.value = true
    profileMsg.value = { NAME_REQUIRED: 'Escribe tu nombre.', INVALID_PHONE: 'El teléfono debe tener 10 dígitos.' }[err.message] || 'No se pudieron guardar tus datos.'
  } finally {
    savingProfile.value = false
  }
}

async function savePassword() {
  pwMsg.value = ''
  pwErr.value = true
  if (!pw.current || !pw.next) {
    pwMsg.value = 'Completa todos los campos.'
    return
  }
  if (pw.next !== pw.repeat) {
    pwMsg.value = 'Las contraseñas nuevas no coinciden.'
    return
  }
  savingPw.value = true
  try {
    await changePortalPassword(pw.current, pw.next)
    pw.current = pw.next = pw.repeat = ''
    pwErr.value = false
    pwMsg.value = 'Contraseña actualizada. Se cerraron tus otras sesiones.'
  } catch (err) {
    pwMsg.value = {
      WEAK_PASSWORD: 'La contraseña nueva debe tener mínimo 8 caracteres, una mayúscula y un número.',
      WRONG_CURRENT_PASSWORD: 'La contraseña actual no es correcta.',
    }[err.message] || 'No se pudo cambiar la contraseña.'
  } finally {
    savingPw.value = false
  }
}

async function onLogout() {
  await logout()
  resetTenantPortal()
  router.push('/login')
}

async function onLogoutAll() {
  await logoutAllPortalSessions()
  state.user = null
  resetTenantPortal()
  router.push('/login')
}
</script>

<style scoped>
.ta-view { flex: 1; overflow-y: auto; background: #fff; padding: 20px 16px 32px; }
.ta-column { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 16px; }
.ta-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.ta-card--row { flex-direction: row; flex-wrap: wrap; gap: 8px; }
.ta-label { margin: 0; font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
.ta-row { display: flex; justify-content: space-between; gap: 16px; }
.ta-key { font: 400 12px Inter, sans-serif; color: #64748b; }
.ta-val { font: 500 13px Inter, sans-serif; color: #1e3a5f; text-align: right; word-break: break-word; }
.ta-field { display: flex; flex-direction: column; gap: 4px; }
.ta-input { height: 36px; padding: 0 10px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 13px Inter, sans-serif; color: #1e3a5f; }
.ta-input:focus { outline: none; border-color: #3b82f6; }
.ta-hint { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.ta-msg { margin: 0; font: 400 12px Inter, sans-serif; color: #15803d; }
.ta-msg--error { color: #b91c1c; }
.ta-btn { align-self: flex-start; height: 36px; padding: 0 14px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: #64748b; font: 500 13px Inter, sans-serif; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.ta-btn:hover { color: #1e3a5f; border-color: #cbd5e1; }
.ta-btn--primary { background: #3b82f6; border-color: #3b82f6; color: #fff; }
.ta-btn--primary:hover { color: #fff; }
.ta-btn:disabled { opacity: 0.6; cursor: not-allowed; }
</style>